import type {
  StoreConversationRequest,
  StoreConversationRequestLinksItem,
  UpdateConversationRequest,
  UpdateConversationRequestLinksItem,
} from "api/generated";
import type {
  ConversationFormData,
  ConversationMediaFormData,
} from "conversations/types";
import { toSchoolClock } from "shared/utils";

/**
 * A create may not carry media ids — there is nothing for them to belong to yet, and the
 * backend rejects the key outright. The id is therefore dropped here rather than sent as
 * null.
 */
const mediaToStoreDto = (
  media: ConversationMediaFormData,
): StoreConversationRequestLinksItem => ({
  name: media.name,
  url: media.url,
});

/**
 * An update carries the id of every link/video it wants to keep; anything omitted is
 * deleted server-side. A new one has no id yet, and the key is left off rather than sent
 * as null, because `filled` rejects an explicit null.
 */
const mediaToUpdateDto = (
  media: ConversationMediaFormData,
): UpdateConversationRequestLinksItem =>
  media.id === null
    ? { name: media.name, url: media.url }
    : { id: media.id, name: media.name, url: media.url };

/**
 * `date` is sent as the Dutch wall-clock reading of the instant ("YYYY-MM-DD HH:mm:ss"),
 * which is what the backend stores. Sending an ISO instant would be read in the app's
 * timezone and shift the time.
 */
const requiredFields = (formData: ConversationFormData) => ({
  student_id: Number(formData.studentId),
  conversation_type_id: Number(formData.conversationTypeId),
  name: formData.name,
  teacher_notes: formData.teacherNotes,
  // The picker holds "" when nothing is chosen; the column is nullable, so "no vak" is
  // sent as null and never as an empty string.
  subject_shortage:
    formData.subjectShortage === "" ? null : formData.subjectShortage,
  date: formData.date === null ? "" : toSchoolClock(formData.date),
});

export const conversationFormDataToStoreDto = (
  formData: ConversationFormData,
): StoreConversationRequest => ({
  ...requiredFields(formData),
  links: formData.links.map(mediaToStoreDto),
  videos: formData.videos.map(mediaToStoreDto),
});

export const conversationFormDataToUpdateDto = (
  formData: ConversationFormData,
): UpdateConversationRequest => ({
  ...requiredFields(formData),
  links: formData.links.map(mediaToUpdateDto),
  videos: formData.videos.map(mediaToUpdateDto),
});
